import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormRow } from '@tanium/react-form';
import { Button } from '@tanium/react-button';
import { Box } from '@tanium/react-box';
import { Notification } from '@tanium/react-persistent-notifications';
import { Stack } from '@tanium/react-stack';
import { LoginFormTextInput, LoginFormButton } from './LoginFormComponents';
import { useAsync, AsyncStatus } from '../../hooks/useAsync';
import { useOnInputChange } from '../../components/InputHandlerUtils';
import { Api } from '../../api';
import { SmallSubtleText } from '../../TextStyles';

export interface ForgotUsernameProps {
  switchToLocalSignin: () => void;
}

export const ForgotUsername: React.VFC<ForgotUsernameProps> = ({ switchToLocalSignin }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const {
    execute: sendEmail,
    status: sendEmailStatus,
    reset: resetEmailSender,
  } = useAsync(() => {
    return Api.tenants.sendRecoverUsernameEmail(email);
  });

  const handleEmailChange = useOnInputChange(setEmail);

  const handleSubmit = useCallback<React.FormEventHandler>(
    async (event) => {
      resetEmailSender();
      event.preventDefault();
      await sendEmail();
    },
    [sendEmail, resetEmailSender]
  );

  return (
    <Box style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box style={{ flexGrow: 1, display: 'flex', alignItems: 'center', width: '100%' }}>
        <form onSubmit={handleSubmit}>
          {sendEmailStatus === AsyncStatus.Success && (
            <Box mb={3}>
              <Notification role="status" type="success">
                {t(
                  'If the email matches an account, any associated usernames have been sent to the specified email address'
                )}
              </Notification>
            </Box>
          )}
          {sendEmailStatus === AsyncStatus.Failure && (
            <Box mb={3}>
              <Notification role="status" type="error">
                {t('Failed to send username recovery email')}
              </Notification>
            </Box>
          )}
          {sendEmailStatus !== AsyncStatus.Success && (
            <>
              <FormRow label={t('Email')} dataKey="email">
                <LoginFormTextInput type="email" value={email} onChange={handleEmailChange} required />
              </FormRow>
              <Box mb={2}>
                <SmallSubtleText>
                  {t(
                    'Enter the email address for your account to receive an email containing usernames associated with that address.'
                  )}
                </SmallSubtleText>
              </Box>
              <LoginFormButton loading={sendEmailStatus === AsyncStatus.Pending}>
                {t('Recover Username')}
              </LoginFormButton>
            </>
          )}
          {sendEmailStatus === AsyncStatus.Success && (
            <Button style={{ width: '100%' }} onClick={switchToLocalSignin}>
              {t('Sign In')}
            </Button>
          )}
        </form>
      </Box>
      <Stack justifyContent="center" style={{ margin: 'auto 0 10px 0' }}>
        {sendEmailStatus === AsyncStatus.Success && (
          <Button variant="link" onClick={resetEmailSender}>
            {t('Retry')}
          </Button>
        )}
        {sendEmailStatus !== AsyncStatus.Success && (
          <Button variant="link" onClick={switchToLocalSignin}>
            {t('Sign In')}
          </Button>
        )}
      </Stack>
    </Box>
  );
};
